<script lang="ts" setup>
import { withoutTrailingSlash } from "ufo";

const route = useRoute();
const { data: page, error } = await useAsyncData(() => {
  const page = queryContent(route.path).findOne();

  if (!page) {
    throw createError({
      statusCode: 404,
      statusMessage: "Not Found",
    });
  }

  return page;
});

if (error.value) {
  throw createError({
    ...error.value,
    fatal: true,
  });
}

const pageWithData = computed(() => {
  return {
    ...page.value,
    appName: useRuntimeConfig().public.appName,
    appAuthor: useRuntimeConfig().public.appAuthor,
    appAuthorAddress: useRuntimeConfig().public.appAuthorAddress,
    contactEmail: useRuntimeConfig().public.contactEmail,
  };
});

const { data: surround } = await useAsyncData(() => {
  return queryContent("blog")
    .only(["title", "description", "_path"])
    .sort({ date: 1 })
    .findSurround(withoutTrailingSlash(route.path));
});

if (page.value)
  useContentHead(page.value);

if (page.value?.image) {
  useSeoMeta({
    ogImage: page.value.image,
  });
}

useHead({
  titleTemplate: "%s", // We want only the page title in posts
});

useSchemaOrg([
  defineArticle({
    // Headline and description is inferred by meta tags
    "@type": ["Article", "BlogPosting"],
    "image": page.value?.image,
    "datePublished": page.value?.date ? new Date(page.value?.date).toISOString() : undefined,
  }),
  defineBreadcrumb({
    "@context": "https://schema.org",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": useRuntimeConfig().public.appName,
        "item": "/",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "/blog",
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": page.value?.title,
        "item": route.path,
      },
    ],
  }),
]);
</script>

<template>
  <UContainer>
    <article>
      <!-- Header -->
      <UPageHeader v-bind="page">
        <template #headline>
          <time
            v-if="page?.date"
            class="text-gray-500 dark:text-gray-400"
          >
            {{ new Date(page.date).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) }}
          </time>
        </template>
      </UPageHeader>

      <UPage>
        <!-- Body Content -->
        <UPageBody prose>
          <ContentRenderer
            v-if="page?.body"
            :value="pageWithData"
          />

          <nav v-if="surround?.length">
            <UDivider class="my-12" />
            <UContentSurround :surround="surround" />
          </nav>
        </UPageBody>

        <!-- Table of Contents -->
        <template
          v-if="page?.body?.toc?.links?.length"
          #right
        >
          <nav aria-label="Table of Contents">
            <UContentToc :links="page.body.toc.links" />
          </nav>
        </template>
      </UPage>
    </article>
  </UContainer>
</template>
